import { useState } from "react";
import { z } from "zod";
import { parseCsv } from "../../company/utils/csvAndDateUtils";

const MAX_CSV_ROWS = 9999;

interface ParsedEmailData {
  validEmails: string[];
  invalidEmails: string[];
  duplicatesInFile: string[];
  alreadyWhitelisted: string[];
}

const emailSchema = z.email({ message: "Invalid email" });

interface UseEmailExtractorReturn {
  parsedData: ParsedEmailData | null;
  isProcessing: boolean;
  fileName: string;
  handleFileSelect: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => Promise<void>;
  extractEmailsFromCsv: (file: File) => Promise<void>;
  reset: () => void;
}

export const useEmailExtractor = (
  existingEmails: string[],
  onEmailsExtracted?: (emails: string[]) => void,
): UseEmailExtractorReturn => {
  const [parsedData, setParsedData] = useState<ParsedEmailData | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [fileName, setFileName] = useState<string>("");

  const extractEmailsFromCsv = async (file: File): Promise<void> => {
    setIsProcessing(true);

    try {
      const text = await readFileAsText(file);
      if (!text) {
        setIsProcessing(false);

        return;
      }

      const rows = parseCsv(text);

      const limitedRows = rows.slice(0, MAX_CSV_ROWS + 1);

      // Extract all emails from all cells
      const allEmails: string[] = [];
      const invalidEmails: string[] = [];

      limitedRows.forEach((row: string[]) => {
        row.forEach((cell: string) => {
          const trimmedCell = cell.trim();
          if (trimmedCell) {
            // Validate email
            const result = emailSchema.safeParse(trimmedCell);
            if (result.success) {
              allEmails.push(trimmedCell.toLowerCase());
            } else if (trimmedCell.includes("@") || trimmedCell.includes(".")) {
              // Only add to invalid if it looks like it could be an email attempt
              invalidEmails.push(trimmedCell);
            }
          }
        });
      });

      // Find duplicates within the file
      const emailCounts = new Map<string, number>();
      allEmails.forEach((email) => {
        emailCounts.set(email, (emailCounts.get(email) ?? 0) + 1);
      });

      const duplicatesInFile = Array.from(emailCounts.entries())
        .filter(([, count]) => count > 1)
        .map(([email]) => email);

      const uniqueEmails = Array.from(new Set(allEmails));

      // Check against existing whitelist
      const existingEmailsSet = new Set(
        existingEmails.map((e) => e.toLowerCase()),
      );
      const alreadyWhitelisted = uniqueEmails.filter((email) =>
        existingEmailsSet.has(email),
      );

      // Final valid emails
      const validEmails = uniqueEmails.filter(
        (email) => !existingEmailsSet.has(email),
      );

      setParsedData({
        validEmails,
        invalidEmails,
        duplicatesInFile,
        alreadyWhitelisted,
      });

      if (onEmailsExtracted && validEmails.length > 0) {
        onEmailsExtracted(validEmails);
      }
    } catch {
      setParsedData(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const readFileAsText = async (file: File): Promise<string | null> => {
    try {
      return await file.text();
    } catch {
      return null;
    }
  };

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    await extractEmailsFromCsv(file);
  };

  const reset = () => {
    setParsedData(null);
    setFileName("");
  };

  return {
    parsedData,
    isProcessing,
    fileName,
    handleFileSelect,
    extractEmailsFromCsv,
    reset,
  };
};
